import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, FileText, ExternalLink } from "lucide-react";
import { recordAcceptance } from "@/lib/legal-utils";
import { useToast } from "@/hooks/use-toast";
import { Badge } from "@/components/ui/badge";

interface LegalDocument {
  document_type: string;
  version: string;
  published_at: string;
  requires_reacceptance: boolean;
  change_summary: string | null;
}

interface LegalReacceptanceModalProps {
  documents: LegalDocument[];
  userId: string;
  onAccepted: () => void;
}

const documentNames: Record<string, string> = {
  terms: "Terms of Service",
  privacy: "Privacy Policy",
  cookie: "Cookie Policy",
  referral_terms: "Referral Terms",
};

const documentUrls: Record<string, string> = {
  terms: "/legal/terms",
  privacy: "/legal/privacy",
  cookie: "/legal/cookies",
  referral_terms: "/legal/referral-terms",
};

export function LegalReacceptanceModal({
  documents,
  userId,
  onAccepted,
}: LegalReacceptanceModalProps) {
  const [loading, setLoading] = useState(false);
  const [acceptedDocs, setAcceptedDocs] = useState<Set<string>>(new Set());
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleAccept = async (docType: string, version: string) => {
    setLoading(true);
    try {
      const success = await recordAcceptance(userId, docType, version);
      if (success) {
        setAcceptedDocs(new Set([...acceptedDocs, docType]));
        
        // If all documents accepted, close modal
        if (acceptedDocs.size + 1 === documents.length) {
          toast({
            title: "Documents Accepted",
            description: "Thank you for reviewing the updated policies.",
          });
          onAccepted();
        }
      } else {
        throw new Error("Failed to record acceptance");
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to record acceptance. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleViewDocument = (docType: string) => {
    const url = documentUrls[docType];
    if (url) {
      window.open(url, "_blank");
    }
  };

  // Filter out already accepted docs
  const pendingDocs = documents.filter(
    (doc) => !acceptedDocs.has(doc.document_type)
  );

  if (pendingDocs.length === 0) {
    return null;
  }

  const currentDoc = pendingDocs[0];

  return (
    <Dialog open={true}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Updated Legal Documents
          </DialogTitle>
          <DialogDescription>
            We've updated our legal documents. Please review and accept to
            continue using PingEarn.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              You must accept these updated documents to continue using our
              services. {documents.length > 1 &&
                `${acceptedDocs.size + 1} of ${documents.length}`}
            </AlertDescription>
          </Alert>

          <div className="p-4 border rounded-lg space-y-3 bg-card">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-semibold text-lg">
                  {documentNames[currentDoc.document_type] ||
                    currentDoc.document_type}
                </h3>
                <div className="flex items-center gap-2 mt-1">
                  <Badge variant="secondary">Version {currentDoc.version}</Badge>
                  <span className="text-sm text-muted-foreground">
                    Published:{" "}
                    {new Date(currentDoc.published_at).toLocaleDateString()}
                  </span>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleViewDocument(currentDoc.document_type)}
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                View Full Document
              </Button>
            </div>

            {currentDoc.change_summary && (
              <div className="mt-3 p-3 bg-muted/50 rounded">
                <p className="text-sm font-medium mb-1">What's Changed:</p>
                <p className="text-sm text-muted-foreground whitespace-pre-line">
                  {currentDoc.change_summary}
                </p>
              </div>
            )}
          </div>

          {documents.length > 1 && (
            <div className="text-sm text-muted-foreground">
              <p>
                Remaining documents: {documents.length - acceptedDocs.size - 1}
              </p>
              <div className="flex flex-wrap gap-2 mt-2">
                {documents.map((doc, idx) => (
                  <Badge
                    key={doc.document_type}
                    variant={
                      acceptedDocs.has(doc.document_type)
                        ? "default"
                        : idx === acceptedDocs.size
                        ? "secondary"
                        : "outline"
                    }
                  >
                    {documentNames[doc.document_type] || doc.document_type}
                    {acceptedDocs.has(doc.document_type) && " ✓"}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            onClick={() =>
              handleAccept(currentDoc.document_type, currentDoc.version)
            }
            disabled={loading}
            className="w-full sm:w-auto"
          >
            {loading ? "Saving..." : "I Accept"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
